import { useAuthStore } from '../store/authStore';
import { isNetworkError } from '../utils/networkUtils';
import { postJsonWithTimeout, FUNCTIONS_URL } from './cloudFunctionsClient';

export { verifyPasscode, verifyContractorLogin, verifyEmployeeLogin } from './loginApi';

/**
 * Register a push notification token for the current user (contractor, employee, or admin)
 * Tokens are stored per userId (not per device) to support multiple users on the same device
 */
export async function registerPushTokenOnServer(token: string, platform: 'ios' | 'android'): Promise<{ success: boolean; error?: string }> {
  try {
    if ((import.meta as any).env?.DEV) {
      console.log(
        '[API] registerPushTokenOnServer called with token (short):',
        token.substring(0, 20) + '...',
        'platform:',
        platform
      );
    }
    
    const jwt = useAuthStore.getState().jwt;
    const authUser = useAuthStore.getState().authUser;

    if ((import.meta as any).env?.DEV) {
      console.log('[API] Auth state - jwt exists:', !!jwt, 'authUser:', authUser);
    }

    if (!jwt || !authUser) {
      console.error('[API] Not authenticated');
      return { success: false, error: 'לא מחובר למערכת' };
    }

    // Extract userId based on userType
    let userId: string | undefined;
    if (authUser.userType === 'contractor' && authUser.contractorId) {
      userId = authUser.contractorId;
    } else if (authUser.userType === 'employee' && authUser.employeeId) {
      userId = authUser.employeeId;
    } else if (authUser.userType === 'admin') {
      // For admin, we can use a fixed identifier or the enteredBy name
      // Since admin doesn't have a unique ID, we'll use 'admin' as the userId
      userId = 'admin';
    }

    if ((import.meta as any).env?.DEV) {
      console.log('[API] Extracted userId:', userId, 'userType:', authUser.userType);
    }

    if (!userId) {
      console.error('[API] Could not extract userId');
      return { success: false, error: 'לא ניתן לזהות משתמש' };
    }

    const requestBody = {
      jwt,
      userId,
      userType: authUser.userType,
      token,
      platform,
    };
    
    if ((import.meta as any).env?.DEV) {
      console.log('[API] Sending request to:', `${FUNCTIONS_URL}/registerPushToken`);
      console.log('[API] Request body:', {
        ...requestBody,
        jwt: jwt ? jwt.substring(0, 12) + '...' : null,
        token: token.substring(0, 12) + '...',
      });
    }

    const data = await postJsonWithTimeout<{ success: boolean; error?: string }>(
      'registerPushToken',
      requestBody as Record<string, unknown>,
    );
    if ((import.meta as any).env?.DEV) {
      console.log('[API] Response data:', data);
    }

    return data;
  } catch (error) {
    console.error('[API] Error registering push token:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה ברישום להתראות' };
  }
}

/**
 * Create or update an entry
 */
export async function upsertEntry(entry: {
  id?: string;
  contractorId: string;
  contractorName: string;
  employeeId: string;
  employeeFullName: string;
  date: string;
  startMinutes: number;
  endMinutes: number;
  note?: string;
  isSickDay?: boolean;
  isHoliday?: boolean;
  /** רק למנהל משרד: סימון רישום שהועתק מעובד אחר */
  copiedFromCoworker?: boolean;
}): Promise<{ success: boolean; entryId?: string; error?: string; existingEntryId?: string; existingEntryData?: any }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    const authUser = useAuthStore.getState().authUser;
    const enteredBy = useAuthStore.getState().enteredBy;
    
    if (!jwt || !authUser) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    // For admin users, use enteredBy when available, otherwise a fixed label.
    // For contractor users, use contractor name.
    let enteredByValue: string;
    let enteredByType: 'admin' | 'contractor';
    
    if (authUser.userType === 'admin') {
      enteredByValue = enteredBy || 'מנהל';
      enteredByType = 'admin';
    } else {
      // Contractor
      enteredByValue = authUser.contractorName || 'קבלן';
      enteredByType = 'contractor';
    }

    return postJsonWithTimeout<{
      success: boolean;
      entryId?: string;
      error?: string;
      existingEntryId?: string;
      existingEntryData?: any;
    }>('upsertEntry', {
      jwt,
      enteredBy: enteredByValue,
      enteredByType: enteredByType,
      entry,
    });
  } catch (error) {
    console.error('Error upserting entry:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בשמירת הנתונים' };
  }
}

/**
 * Delete an entry
 */
export async function deleteEntry(entryId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('deleteEntry', {
      jwt,
      entryId,
    });
  } catch (error) {
    console.error('Error deleting entry:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה במחיקת הנתונים' };
  }
}

/**
 * Approve an entry by employee
 */
export async function approveEntry(entryId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('approveEntry', {
      jwt,
      entryId,
    });
  } catch (error) {
    console.error('Error approving entry:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה באישור הרשומה' };
  }
}

/**
 * Dispute an entry by employee
 */
export async function disputeEntry(
  entryId: string,
  disputedStartMinutes: number,
  disputedEndMinutes: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('disputeEntry', {
      jwt,
      entryId,
      disputedStartMinutes,
      disputedEndMinutes,
    });
  } catch (error) {
    console.error('Error disputing entry:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בערעור על הרשומה' };
  }
}

/**
 * Get employee alerts (entries pending approval, within the server alert window; stale rows are auto-approved server-side)
 */
export async function getEmployeeAlerts(): Promise<{
  success: boolean;
  data?: Array<{
    entryId: string;
    date: string;
    startMinutes: number;
    endMinutes: number;
    isSickDay: boolean;
    isHoliday: boolean;
    note?: string;
    createdAt: any;
  }>;
  error?: string;
}> {
  try {
    const jwt = useAuthStore.getState().jwt;
    const authUser = useAuthStore.getState().authUser;
    
    if (!jwt || !authUser || authUser.userType !== 'employee' || !authUser.employeeId) {
      return { success: false, error: 'לא מחובר כעובד' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      data?: Array<{
        entryId: string;
        date: string;
        startMinutes: number;
        endMinutes: number;
        isSickDay: boolean;
        isHoliday: boolean;
        note?: string;
        createdAt: any;
      }>;
      error?: string;
    }>('getEmployeeAlerts', {
      jwt,
      employeeId: authUser.employeeId,
    });
  } catch (error) {
    console.error('Error getting employee alerts:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בקבלת התראות' };
  }
}

/**
 * Get contractor alerts (disputed entries by employees)
 */
export async function getContractorAlerts(): Promise<{
  success: boolean;
  data?: Array<{
    entryId: string;
    date: string;
    employeeId: string;
    employeeFullName: string;
    employeePassportNumber: string;
    startMinutes: number;
    endMinutes: number;
    disputedStartMinutes: number;
    disputedEndMinutes: number;
    disputedAt: any;
    isSickDay: boolean;
    isHoliday: boolean;
    disputeResolved?: boolean;
    disputeRejected?: boolean;
  }>;
  error?: string;
}> {
  try {
    const jwt = useAuthStore.getState().jwt;
    const authUser = useAuthStore.getState().authUser;
    
    if (!jwt || !authUser || authUser.userType !== 'contractor' || !authUser.contractorId) {
      return { success: false, error: 'לא מחובר כקבלן' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      data?: Array<{
        entryId: string;
        date: string;
        employeeId: string;
        employeeFullName: string;
        employeePassportNumber: string;
        startMinutes: number;
        endMinutes: number;
        disputedStartMinutes: number;
        disputedEndMinutes: number;
        disputedAt: any;
        isSickDay: boolean;
        isHoliday: boolean;
        disputeResolved?: boolean;
        disputeRejected?: boolean;
      }>;
      error?: string;
    }>('getContractorAlerts', {
      jwt,
      contractorId: authUser.contractorId,
    });
  } catch (error) {
    console.error('Error getting contractor alerts:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בקבלת התראות' };
  }
}

/**
 * Resolve (fix) a dispute by contractor
 */
export async function resolveDispute(entryId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('resolveDispute', {
      jwt,
      entryId,
    });
  } catch (error) {
    console.error('Error resolving dispute:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בתיקון הערעור' };
  }
}

/**
 * Reject a dispute by contractor
 */
export async function rejectDispute(entryId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('rejectDispute', {
      jwt,
      entryId,
    });
  } catch (error) {
    console.error('Error rejecting dispute:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בדחיית הערעור' };
  }
}

/**
 * Resolve a dispute by admin
 */
export async function resolveDisputeByAdmin(entryId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('resolveDisputeByAdmin', {
      jwt,
      entryId,
    });
  } catch (error) {
    console.error('Error resolving dispute by admin:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בסימון הערעור כטופל' };
  }
}

/**
 * Create a new contractor
 */
export async function createContractor(contractor: {
  name: string;
  vatNumber: string;
  contactName: string;
  contractorNumber?: string;
}): Promise<{ success: boolean; contractorId?: string; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; contractorId?: string; error?: string }>(
      'createContractor',
      { jwt, contractor } as Record<string, unknown>,
    );
  } catch (error) {
    console.error('Error creating contractor:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה ביצירת קבלן' };
  }
}

/**
 * Export entries to XLSX
 */
export async function exportEntries(params: {
  contractorId?: string;
  employeeId?: string;
  startDate: string;
  endDate: string;
}): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; url?: string; error?: string }>(
      'exportEntries',
      { jwt, ...params } as Record<string, unknown>,
    );
  } catch (error) {
    console.error('Error exporting entries:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בייצוא הנתונים' };
  }
}

/**
 * Generate employee monthly report as PDF
 */
export async function generateEmployeeReportPDF(params: {
  employeeId: string;
  yearMonth: string;
}): Promise<{ success: boolean; html?: string; filename?: string; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      html?: string;
      filename?: string;
      error?: string;
    }>('generateEmployeeReportPDF', { jwt, ...params } as Record<string, unknown>);
  } catch (error) {
    console.error('Error generating PDF report:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה ביצירת דוח PDF' };
  }
}

/**
 * Generate employee monthly report as Excel
 */
export async function generateEmployeeReportExcel(params: {
  employeeId: string;
  yearMonth: string;
}): Promise<{ success: boolean; excel?: string; filename?: string; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      excel?: string;
      filename?: string;
      error?: string;
    }>('generateEmployeeReportExcel', { jwt, ...params } as Record<string, unknown>);
  } catch (error) {
    console.error('Error generating Excel report:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה ביצירת דוח Excel' };
  }
}

/**
 * Create a new employee
 */
export async function createEmployee(employee: {
  firstName: string;
  lastName: string;
  passportNumber: string;
  employeeNumber?: string;
  contractorId?: string;
  contractorName?: string;
}): Promise<{ success: boolean; employeeId?: string; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; employeeId?: string; error?: string }>(
      'createEmployee',
      { jwt, employee } as Record<string, unknown>,
    );
  } catch (error) {
    console.error('Error creating employee:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה ביצירת עובד' };
  }
}

/**
 * Update an existing employee
 */
export async function updateEmployee(employeeId: string, employee: {
  firstName: string;
  lastName: string;
  passportNumber: string;
  employeeNumber?: string;
  password?: string | null; // null means remove password
}): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('updateEmployee', {
      jwt,
      employeeId,
      employee,
    } as Record<string, unknown>);
  } catch (error) {
    console.error('Error updating employee:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעדכון עובד' };
  }
}

/**
 * Update employee contractors (main + sub-contractors)
 */
export async function updateEmployeeContractors(
  employeeId: string,
  contractors: {
    contractorId: string;
    contractorName: string;
    subContractorId1?: string | null;
    subContractorName1?: string | null;
    subContractorId2?: string | null;
    subContractorName2?: string | null;
    subContractorId3?: string | null;
    subContractorName3?: string | null;
    subContractorId4?: string | null;
    subContractorName4?: string | null;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('updateEmployeeContractors', {
      jwt,
      employeeId,
      contractors,
    } as Record<string, unknown>);
  } catch (error) {
    console.error('Error updating employee contractors:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעדכון שיוכי קבלנים' };
  }
}

/**
 * Update employee assignment (which contractor the employee is currently assigned to for attendance)
 */
export async function updateEmployeeAssignment(
  employeeId: string,
  assignedContractorId: string | null,
  assignedContractorName: string | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('updateEmployeeAssignment', {
      jwt,
      employeeId,
      assignedContractorId,
      assignedContractorName,
    } as Record<string, unknown>);
  } catch (error) {
    console.error('Error updating employee assignment:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעדכון שיבוץ' };
  }
}

/**
 * Delete an employee (hard delete - only from archive)
 */
export async function deleteEmployee(employeeId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('deleteEmployee', {
      jwt,
      employeeId,
    });
  } catch (error) {
    console.error('Error deleting employee:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה במחיקת עובד' };
  }
}

/**
 * Archive an employee
 * Moves employee and all their entries to archive
 */
export async function archiveEmployee(employeeId: string): Promise<{ success: boolean; error?: string; archivedEntriesCount?: number }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      error?: string;
      archivedEntriesCount?: number;
    }>('archiveEmployee', { jwt, employeeId });
  } catch (error) {
    console.error('Error archiving employee:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בהעברת עובד לארכיון' };
  }
}

/**
 * Restore an employee from archive
 * Moves employee and all their entries back from archive
 */
export async function restoreEmployee(employeeId: string): Promise<{ success: boolean; error?: string; restoredEntriesCount?: number }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      error?: string;
      restoredEntriesCount?: number;
    }>('restoreEmployee', { jwt, employeeId });
  } catch (error) {
    console.error('Error restoring employee:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בהחזרת עובד מהארכיון' };
  }
}

/**
 * Permanently delete an employee from archive
 * Hard delete of employee and all their entries from archive
 */
export async function permanentlyDeleteEmployee(employeeId: string): Promise<{ success: boolean; error?: string; deletedEntriesCount?: number }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      error?: string;
      deletedEntriesCount?: number;
    }>('permanentlyDeleteEmployee', { jwt, employeeId });
  } catch (error) {
    console.error('Error permanently deleting employee:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה במחיקת עובד מהארכיון' };
  }
}

/**
 * Update an existing contractor
 */
export async function updateContractor(contractorId: string, contractor: {
  name: string;
  vatNumber: string;
  contactName: string;
  contractorNumber?: string;
  password?: string | null; // null means remove password
}): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('updateContractor', {
      jwt,
      contractorId,
      contractor,
    } as Record<string, unknown>);
  } catch (error) {
    console.error('Error updating contractor:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעדכון קבלן' };
  }
}

/**
 * Delete a contractor (hard delete with cascading)
 */
export async function deleteContractor(contractorId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('deleteContractor', {
      jwt,
      contractorId,
    });
  } catch (error) {
    console.error('Error deleting contractor:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה במחיקת קבלן' };
  }
}

/**
 * Archive a contractor
 * Moves contractor and all associated employees and entries to archive
 */
export async function archiveContractor(contractorId: string): Promise<{ success: boolean; error?: string; archivedEmployeesCount?: number; archivedEntriesCount?: number }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      error?: string;
      archivedEmployeesCount?: number;
      archivedEntriesCount?: number;
    }>('archiveContractor', { jwt, contractorId });
  } catch (error) {
    console.error('Error archiving contractor:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בהעברת קבלן לארכיון' };
  }
}

/**
 * Restore a contractor from archive
 * Moves contractor and all associated employees and entries back from archive
 */
export async function restoreContractor(contractorId: string): Promise<{ success: boolean; error?: string; restoredEmployeesCount?: number; restoredEntriesCount?: number }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      error?: string;
      restoredEmployeesCount?: number;
      restoredEntriesCount?: number;
    }>('restoreContractor', { jwt, contractorId });
  } catch (error) {
    console.error('Error restoring contractor:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בהחזרת קבלן מהארכיון' };
  }
}

/**
 * Permanently delete a contractor from archive
 * Deletes the contractor and all associated employees and entries from archive (hard delete)
 */
export async function permanentlyDeleteContractor(contractorId: string): Promise<{ success: boolean; error?: string; deletedEmployeesCount?: number; deletedEntriesCount?: number }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      error?: string;
      deletedEmployeesCount?: number;
      deletedEntriesCount?: number;
    }>('permanentlyDeleteContractor', { jwt, contractorId });
  } catch (error) {
    console.error('Error permanently deleting contractor:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה במחיקת קבלן מהארכיון' };
  }
}

/**
 * Transfer an employee to a different contractor
 */
export async function transferEmployee(employeeId: string, newContractorId: string): Promise<{ success: boolean; updatedEntriesCount?: number; newContractorName?: string; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      updatedEntriesCount?: number;
      newContractorName?: string;
      error?: string;
    }>('transferEmployee', { jwt, employeeId, newContractorId });
  } catch (error) {
    console.error('Error transferring employee:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בהעברת עובד' };
  }
}

/**
 * Allow or block contractor access to the contractor web interface (admin only).
 * HTTPS name remains toggleContractorBlock for deployment continuity.
 */
export async function setContractorInterfaceAccess(
  contractorId: string,
  interfaceAccessGranted: boolean,
): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;

    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('toggleContractorBlock', {
      jwt,
      contractorId,
      interfaceAccessGranted,
    });
  } catch (error) {
    console.error('Error setting contractor interface access:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעדכון גישת קבלן' };
  }
}

/**
 * Admin: remove contractor web-login password (contractor can log in with ח.פ only until a new password is set).
 */
export async function resetContractorPassword(contractorId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;

    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('resetContractorPassword', {
      jwt,
      contractorId,
    });
  } catch (error) {
    console.error('Error resetting contractor password:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה באיפוס סיסמת קבלן' };
  }
}

/**
 * Allow or block employee access to the employee web interface (admin only).
 */
export async function setEmployeeInterfaceAccess(
  employeeId: string,
  interfaceAccessGranted: boolean,
): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;

    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('setEmployeeInterfaceAccess', {
      jwt,
      employeeId,
      interfaceAccessGranted,
    });
  } catch (error) {
    console.error('Error setting employee interface access:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעדכון גישת עובד' };
  }
}

/**
 * Admin: remove employee web-login password (employee can log in with passport only until a new password is set).
 */
export async function resetEmployeePassword(employeeId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;

    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('resetEmployeePassword', {
      jwt,
      employeeId,
    });
  } catch (error) {
    console.error('Error resetting employee password:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה באיפוס סיסמת עובד' };
  }
}

/**
 * Create a new issue report
 */
export async function createIssue(issue: {
  subject: string;
  description: string;
}): Promise<{ success: boolean; issueId?: string; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    const authUser = useAuthStore.getState().authUser;
    
    if (!jwt || !authUser) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; issueId?: string; error?: string }>('createIssue', {
      jwt,
      issue,
    } as Record<string, unknown>);
  } catch (error) {
    console.error('Error creating issue:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בשליחת הפניה' };
  }
}

/**
 * Update issue status
 */
export async function updateIssueStatus(issueId: string, status: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('updateIssueStatus', {
      jwt,
      issueId,
      status,
    });
  } catch (error) {
    console.error('Error updating issue status:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעדכון סטטוס הפניה' };
  }
}

/**
 * Get admin statistics
 */
export async function getAdminStats(): Promise<{ 
  success: boolean; 
  data?: {
    totalContractors: number;
    totalEmployees: number;
    activeContractors: number;
    totalHoursEntered: number;
    topContractors: Array<{ contractorId: string; contractorName: string; totalHours: number }>;
  };
  error?: string 
}> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      data?: {
        totalContractors: number;
        totalEmployees: number;
        activeContractors: number;
        totalHoursEntered: number;
        topContractors: Array<{ contractorId: string; contractorName: string; totalHours: number }>;
      };
      error?: string;
    }>('getAdminStats', { jwt });
  } catch (error) {
    console.error('Error getting admin stats:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בקבלת סטטיסטיקות' };
  }
}

/**
 * Update contractor password (contractor can update their own password)
 */
export async function updateContractorPassword(currentPassword: string | undefined, newPassword: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    const authUser = useAuthStore.getState().authUser;
    
    if (!jwt || !authUser || authUser.userType !== 'contractor' || !authUser.contractorId) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('updateContractorPassword', {
      jwt,
      contractorId: authUser.contractorId,
      ...(currentPassword !== undefined ? { currentPassword } : {}),
      newPassword,
    } as Record<string, unknown>);
  } catch (error) {
    console.error('Error updating contractor password:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעדכון סיסמה' };
  }
}

/**
 * Update employee password (employee can update their own password)
 */
export async function updateEmployeePassword(currentPassword: string | undefined, newPassword: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    const authUser = useAuthStore.getState().authUser;

    if (!jwt || !authUser || authUser.userType !== 'employee' || !authUser.employeeId) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('updateEmployeePassword', {
      jwt,
      ...(currentPassword !== undefined ? { currentPassword } : {}),
      newPassword,
    } as Record<string, unknown>);
  } catch (error) {
    console.error('Error updating employee password:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעדכון סיסמה' };
  }
}

/**
 * Start attendance tracking for an employee
 */
export async function startAttendance(location?: { latitude: number; longitude: number; accuracy?: number }): Promise<{ 
  success: boolean; 
  sessionId?: string; 
  startTime?: number;
  error?: string 
}> {
  try {
    const jwt = useAuthStore.getState().jwt;
    const authUser = useAuthStore.getState().authUser;
    
    if (!jwt || !authUser || authUser.userType !== 'employee' || !authUser.employeeId) {
      return { success: false, error: 'לא מחובר כעובד' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      sessionId?: string;
      startTime?: number;
      error?: string;
    }>('startAttendance', {
      jwt,
      employeeId: authUser.employeeId,
      ...(location !== undefined ? { location } : {}),
    });
  } catch (error) {
    console.error('Error starting attendance:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בהפעלת נוכחות' };
  }
}

/**
 * Stop attendance tracking and create entry
 */
export async function stopAttendance(
  sessionId: string,
  location?: { latitude: number; longitude: number; accuracy?: number },
  options?: { stoppedByFieldWorker?: boolean; discardSession?: boolean },
): Promise<{
  success: boolean;
  entryId?: string;
  cancelled?: boolean;
  message?: string;
  error?: string;
}> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      entryId?: string;
      cancelled?: boolean;
      message?: string;
      error?: string;
    }>('stopAttendance', {
      jwt,
      sessionId,
      ...(location !== undefined ? { location } : {}),
      ...(options?.stoppedByFieldWorker ? { stoppedByFieldWorker: true } : {}),
      ...(options?.discardSession ? { discardSession: true } : {}),
    });
  } catch (error) {
    console.error('Error stopping attendance:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בעצירת נוכחות' };
  }
}

/**
 * Fix (modify) attendance entry hours by contractor
 */
export async function fixAttendanceEntry(entryId: string, startMinutes: number, endMinutes: number): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('fixAttendanceEntry', {
      jwt,
      entryId,
      startMinutes,
      endMinutes,
    });
  } catch (error) {
    console.error('Error fixing attendance entry:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בתיקון הרשומה' };
  }
}

/**
 * Approve attendance entry by contractor (approve as-is without modification)
 */
export async function approveAttendanceEntry(entryId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const jwt = useAuthStore.getState().jwt;
    
    if (!jwt) {
      return { success: false, error: 'לא מחובר למערכת' };
    }

    return postJsonWithTimeout<{ success: boolean; error?: string }>('approveAttendanceEntry', {
      jwt,
      entryId,
    });
  } catch (error) {
    console.error('Error approving attendance entry:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה באישור הרשומה' };
  }
}

/**
 * Get active attendance sessions for a contractor
 */
export async function getActiveAttendance(): Promise<{
  success: boolean;
  data?: {
    activeSessions: Array<{
      id: string;
      employeeId: string;
      employeeFullName: string;
      startTime: any;
      date: string;
    }>;
    notWorkingCount: number;
  };
  error?: string;
}> {
  try {
    const jwt = useAuthStore.getState().jwt;
    const authUser = useAuthStore.getState().authUser;
    
    if (!jwt || !authUser || authUser.userType !== 'contractor' || !authUser.contractorId) {
      return { success: false, error: 'לא מחובר כקבלן' };
    }

    return postJsonWithTimeout<{
      success: boolean;
      data?: {
        activeSessions: Array<{
          id: string;
          employeeId: string;
          employeeFullName: string;
          startTime: any;
          date: string;
        }>;
        notWorkingCount: number;
      };
      error?: string;
    }>('getActiveAttendance', {
      jwt,
      contractorId: authUser.contractorId,
    });
  } catch (error) {
    console.error('Error getting active attendance:', error);
    if (isNetworkError(error)) {
      return { success: false, error: 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.' };
    }
    return { success: false, error: 'שגיאה בטעינת נוכחות פעילה' };
  }
}


